import { useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { User, UserPlus, ArrowLeft } from "lucide-react";

/**
 * Post-Google chooser for brand-new users.
 * Routes to /register (guest) or /member-register (member) with prefilled Google data in navigation state.
 */
export default function GoogleContinue() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantSlug } = useParams();
  const google = location.state?.google;

  useEffect(() => {
    if (!google) {
      navigate(`/${tenantSlug}/login`, { replace: true });
      return;
    }
    // Skip the Register-as-Guest / Apply-as-Member chooser when the
    // Google flow was explicitly initiated from a "Become a Member" CTA
    // (GoogleAuthButton stamped `google_signup_intent=member` before the
    // OAuth redirect). Auto-route to /member-register with Google prefill.
    try {
      const intent = sessionStorage.getItem("google_signup_intent");
      if (intent === "member") {
        sessionStorage.removeItem("google_signup_intent");
        navigate(`/${tenantSlug}/member-register`, {
          replace: true,
          state: { google },
        });
      }
    } catch { /* ignore */ }
  }, [google, navigate, tenantSlug]);

  if (!google) return null;

  const go = (role) => {
    if (role === "member") {
      // Google-auth members go through the SAME full MemberRegister flow
      // (including OTP re-verification of email) — only email + name come
      // pre-filled from Google.
      navigate(`/${tenantSlug}/member-register`, { state: { google } });
    } else {
      navigate(`/${tenantSlug}/register`, { state: { google } });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 px-4 py-10">
      <div className="max-w-md mx-auto" data-testid="google-continue">
        <button
          onClick={() => navigate(`/${tenantSlug}/login`)}
          className="flex items-center gap-2 text-slate-500 text-sm mb-6 hover:text-slate-700"
        >
          <ArrowLeft className="w-4 h-4" /> Cancel
        </button>

        <div className="bg-white rounded-3xl shadow-lg p-8">
          <h1 className="text-2xl font-extrabold text-[#1e3a5f] mb-2">Welcome, {google.name?.split(" ")[0] || "there"}!</h1>
          <p className="text-slate-500 text-sm mb-6">
            Signed in as <strong>{google.email}</strong>. Choose how you&apos;d like to continue with My Maitree.
          </p>

          <div className="space-y-3">
            <button
              onClick={() => go("guest")}
              className="w-full flex items-start gap-4 p-5 rounded-2xl border-2 border-slate-200 hover:border-[#0ea5e9] hover:bg-[#0ea5e9]/5 text-left transition-colors"
              data-testid="google-choose-guest"
            >
              <User className="w-6 h-6 text-[#0ea5e9] flex-shrink-0 mt-1" />
              <div>
                <p className="font-bold text-[#1e3a5f]">Register as Guest</p>
                <p className="text-xs text-slate-500 mt-1">Attend events, top up a wallet, use vouchers. Quick registration.</p>
              </div>
            </button>

            <button
              onClick={() => go("member")}
              className="w-full flex items-start gap-4 p-5 rounded-2xl border-2 border-slate-200 hover:border-[#2d8a4e] hover:bg-[#2d8a4e]/5 text-left transition-colors"
              data-testid="google-choose-member"
            >
              <UserPlus className="w-6 h-6 text-[#2d8a4e] flex-shrink-0 mt-1" />
              <div>
                <p className="font-bold text-[#1e3a5f]">Apply as Member</p>
                <p className="text-xs text-slate-500 mt-1">Full membership benefits. Requires FA approval and additional details.</p>
              </div>
            </button>
          </div>

          <p className="text-[11px] text-slate-400 mt-6 text-center">You&apos;ll fill in a short form on the next screen. No password needed — we&apos;ll use Google to sign you in.</p>
        </div>
      </div>
    </div>
  );
}
